import React, { useState, useEffect, useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckSquare, faAngleDown } from '@fortawesome/free-solid-svg-icons';
import { faSquare } from '@fortawesome/free-regular-svg-icons';
import 'bulma/css/bulma.min.css';

import { ScanTypeSlug, ScanType, Parameter } from '../../scanTypes.ts';
import { BeamlineContext, BeamlineLoadingState } from '../../beamlineProvider.tsx';
import { LoadingBanner, LoadingState } from '../../components/loadingBanner.tsx';
import { ParameterSettingsDto, updateParameterSettings } from '../../api/api.ts';
import { Coordinates, EditableTable, EditableTableCellSettings } from '../../components/editableTable.tsx';
import { CellValidationStatus, CellValidationResult } from '../../components/editableTableCell/cellDto.ts';
import AddParameterSettings from './addParameterSettings.tsx';
import RemoveParameterSettings from './removeParameterSettings.tsx';
import { Uid, truthyJoin, sortWithNumberParsing } from '../../utils.tsx';


interface ParameterTableProps {
  scantypeslug: ScanTypeSlug;
}

// It's a magic number I know, and that's annoying.
// This is the number of columns to display in the table.
const FIXED_COLUMN_COUNT = 7;


// This is a standard data structure used to indicate the selection of
// a single field, or parameter, in a SampleConfiguration object.
// For example the sample editing table has columns that refer to fields like "Name", "Description", etc,
// but also has columns for parameters.  This structure can be used to indicate one of those columns.
enum ParameterSettingsField {
  Id, Name, Description, CustomDefault, ReadOnly, Required, DisplayOrder
};
const parameterSettingsFieldDisplayOrder: ParameterSettingsField[] = [
  ParameterSettingsField.Id, ParameterSettingsField.Name, ParameterSettingsField.Description, ParameterSettingsField.CustomDefault,
  ParameterSettingsField.ReadOnly, ParameterSettingsField.Required, ParameterSettingsField.DisplayOrder
];
const columnNames = [
  "Id", "Name", "Description", "Custom Default", "Read Only", "Required", "Display Order"
];


const ParameterTable: React.FC<ParameterTableProps> = (props) => {

  const beamlineContext = useContext(BeamlineContext);

  const [sortedParameterSettingsIds, setSortedParameterSettingsIds] = useState<Uid[]>([]);
  const [selectedParameterSettingsIds, setSelectedParameterSettingsIds] = useState<Uid[]>([]);
  // We hide the Id by default
  const [hiddenColumnSet, setHiddenColumnSet] = useState<Set<number>>(
    new Set([ParameterSettingsField.Id]));

  const [tableSortColumn, setTableSortColumn] = useState<ParameterSettingsField>(ParameterSettingsField.Name);
  const [tableSortReverse, setTableSortReverse] = useState<boolean>(false);
  

  function getScanType(): ScanType | undefined {
    if (!props.scantypeslug) { return undefined; }
    if (beamlineContext.loadingState != BeamlineLoadingState.Succeeded) { return undefined; }
    return beamlineContext.scanTypes.typesBySlug.get(props.scantypeslug);
  }


  useEffect(() => {
    const thisScanType = getScanType();
    if (!thisScanType) { return; }
    setSortedParameterSettingsIds(thisScanType.parameterIds);
  }, [props.scantypeslug, beamlineContext.changeCounter, beamlineContext.loadingState]);


  function sortParameterSettingsIds(field: ParameterSettingsField, reverse: boolean): Uid[] {
    const thisScanType = getScanType();
    if (!thisScanType) { return []; }
    const parameterSettings = Array.from(thisScanType.parameterSettingsById.values());
    const parameters = thisScanType.parameterIds.map((id) => beamlineContext.scanTypes.parametersById.get(id)!);
    var sortedSettings:ParameterSettingsDto[] = [];
    var sorted:Parameter[] = [];
    switch (field) {
      case ParameterSettingsField.Id:
        sorted = parameters.sort((a, b) => { return sortWithNumberParsing(a.slug, b.slug, reverse)});
        return sorted.map((s) => s.id);
      case ParameterSettingsField.Name:
        sorted = parameters.sort((a, b) => { return sortWithNumberParsing(a.name, b.name, reverse)});
        return sorted.map((s) => s.id);
      case ParameterSettingsField.Description:
        sorted = parameters.sort((a, b) => { return sortWithNumberParsing(a.description, b.description, reverse)});
        return sorted.map((s) => s.id);
      case ParameterSettingsField.CustomDefault:
        sortedSettings = parameterSettings.sort((a, b) => { return sortWithNumberParsing(a.default, b.default, reverse)});
        return sortedSettings.map((s) => s.id_parameter);
      case ParameterSettingsField.ReadOnly:
        sortedSettings = parameterSettings.sort(
            (a, b) => { return a.read_only === b.read_only ? 0 : (reverse ? b.read_only : a.read_only) ? -1 : 1 }
          );
        return sortedSettings.map((s) => s.id_parameter);
      case ParameterSettingsField.Required:
        sortedSettings = parameterSettings.sort((a, b) => { return a.required === b.required ? 0 : (reverse ? b.required : a.required) ? -1 : 1 });
        return sortedSettings.map((s) => s.id_parameter);
      case ParameterSettingsField.DisplayOrder:
        sorted = parameters.sort((a, b) => { return reverse ? b.order - a.order : a.order - b.order });
        return sorted.map((s) => s.id);
    }
  }


  function clickedTableSortHeader(f:ParameterSettingsField) {
    const sortDirection = (f == tableSortColumn) ? !tableSortReverse : tableSortReverse;
    setTableSortColumn(f);
    setTableSortReverse(sortDirection);
    setSortedParameterSettingsIds(sortParameterSettingsIds(f, sortDirection));
  }


  function toggleHiddenColumn(index: number) {
    const newHiddenColumnSet = new Set(hiddenColumnSet);
    if (hiddenColumnSet.has(index)) {
      newHiddenColumnSet.delete(index);
    } else {
      newHiddenColumnSet.add(index);
    }
    setHiddenColumnSet(newHiddenColumnSet);
  }


  const scanType = getScanType();
  if (!scanType) {
    return (<LoadingBanner state={LoadingState.Failure} message="Scan Type not found."></LoadingBanner>)
  }


  // Stubbed for now
  function tableOnCopy(event: React.ClipboardEvent, rectLower: Coordinates, rectHigher: Coordinates) {
    return;
  }


  // Stubbed for now
  function tableOnPaste(event: React.ClipboardEvent, rectLower: Coordinates, rectHigher: Coordinates) {
    return;
  }


  // Add the vector to the given currentPosition until it points to a table cell that is
  // valid for editing, then return that position, or return null if we run off the edge of the table.
  function isCellValidForEditing(coordinates: Coordinates): boolean {
    const fieldType = parameterSettingsFieldDisplayOrder[coordinates.x];
    if (fieldType == ParameterSettingsField.Id || fieldType == ParameterSettingsField.Name || fieldType == ParameterSettingsField.Description || fieldType == ParameterSettingsField.DisplayOrder) {
      return false;
    }
    return true;
  }


  // A function passed to every EditableTableCell (non-header table cell in samples table)
  // and used when rendering the table,
  // that validates the given input value based on its cell's x,y location in the table.
  // It relies on the displayedParameters constant, calculated just above.
  function cellValidator(x: number, y: number, value: any): CellValidationResult {
    return { status: CellValidationStatus.Success, message: null };
  }


  // A function passed to every EditableTableCell (non-header table cell in samples table)
  // that saves the given input value to the SampleConfiguration indicated by the
  // table cell at the given x,y location.
  // It relies on the displayedParameters constant, calculated just above.
  function cellSave(x: number, y: number, newValue: any): CellValidationResult {

    // When editing Parameters, we refuse to save any value that doesn't pass validation.
    const validationResult = cellValidator(x, y, newValue);
    if (validationResult.status != CellValidationStatus.Success) {
      return validationResult;
    }

    const thisScanType = getScanType();
    if (!thisScanType) { return { status: CellValidationStatus.Failure, message: null }; }

    const thisParamSettings = thisScanType.parameterSettingsById.get(sortedParameterSettingsIds[y]);
    if (!thisParamSettings) {
      return { status: CellValidationStatus.Failure, message: "Error: Parameter settings entry does not exist!" }
    }

    switch (parameterSettingsFieldDisplayOrder[x]) {
      case ParameterSettingsField.CustomDefault:
        thisParamSettings.default = (newValue as string).trim() == "" ? undefined : newValue;
        break;
      case ParameterSettingsField.ReadOnly:
        thisParamSettings.read_only = newValue as boolean;
        break;
      case ParameterSettingsField.Required:
        thisParamSettings.required = newValue as boolean;
        break;
      default:
        break;
    }

    // This is an async call but we're not going to wait for it to finish!
    updateParameterSettings(thisParamSettings);
    beamlineContext.changed();

    return { status: CellValidationStatus.Success, message: null };
  }


  function tableRowSelectionChanged(rowIndexes: number[]) {
    const thisScanType = getScanType();
    if (!thisScanType) { return; }
    var newSelectedParameterSettingsIds = rowIndexes.map((i) => {
      const thisParamSettings = thisScanType?.parameterSettingsById.get(sortedParameterSettingsIds[i])!;
      return thisParamSettings.id;
    });
    setSelectedParameterSettingsIds(newSelectedParameterSettingsIds);
  }


  function renderHeaderCell(x: number): React.JSX.Element {
    const sortDirectionStyle = tableSortReverse ? "sortedup" : "sorteddown";
    const fieldType = parameterSettingsFieldDisplayOrder[x];
    const fieldName = columnNames[x];

    return (<th key={fieldName}
            className={truthyJoin("sortable", (tableSortColumn == fieldType) && sortDirectionStyle)}
            onClick={ () => clickedTableSortHeader(fieldType) }
            scope="col">{fieldName}</th>);
  }


  // Function provided to the EditableTable.  Given coordinates, renders an editable cell.
  // The editable cell is given the activationStatus and cellFunctions prepared by the EditableTable.
  function getTableCellSettings(coordinates: Coordinates): EditableTableCellSettings {
    const thisParam = beamlineContext.scanTypes.parametersById.get(sortedParameterSettingsIds[coordinates.y])!;
    const thisScanType = getScanType();
    const thisParamSettings = thisScanType?.parameterSettingsById.get(thisParam.id);
    if (!thisParam || !thisScanType || !thisParamSettings) {
      return {
        key: `${coordinates.x}-${coordinates.y}`,
        isInvalid: false,
        isReadOnly: true,
        isUnused: true,
        value: null
      };
    }

    // Make a lookup table of values, then pass the appropriate one to the validator.
    // If validation doesn't pass, set a flag that will be applied to the call.
    const valuesArray = [
      thisParam.slug, thisParam.name, thisParam.description, thisParamSettings.default,
      thisParamSettings.read_only, thisParamSettings.required, thisParam.order
    ];
    const cellValue = valuesArray[coordinates.x];
    const validationResult = cellValidator(coordinates.x, coordinates.y, cellValue);
    const isInvalid = validationResult.status == CellValidationStatus.Success ? false : true;

    // Standard settings for every cell in the row.
    const cellSettings:EditableTableCellSettings = {
      key: `${coordinates.x}-${coordinates.y}`,
      isInvalid: isInvalid,
      isReadOnly: false,
      isUnused: false,
      value: undefined
    }

    // Set the value for the cell, making any conversions needed, and apply additional per-column settings.
    switch (parameterSettingsFieldDisplayOrder[coordinates.x]) {
      case ParameterSettingsField.Id:
        cellSettings.isReadOnly = true;
        cellSettings.value = thisParam.slug;
        cellSettings.description="Parameter ID, for use with the API.";
        break;
      case ParameterSettingsField.Name:
        cellSettings.isReadOnly = true;
        cellSettings.value = thisParam.name;
        cellSettings.description="Parameter name.";
        break;
      case ParameterSettingsField.Description:
        cellSettings.isReadOnly = true;
        cellSettings.value = thisParam.description ?? "";
        cellSettings.description="Parameter description.";
        break;
      case ParameterSettingsField.CustomDefault:
        cellSettings.value = thisParamSettings.default ?? "";
        cellSettings.description="Custom default value for the Parameter when used in this Scan Type. If left blank, the standard default will be used.";
        break;
      case ParameterSettingsField.ReadOnly:
        cellSettings.isBoolean = true;
        cellSettings.value = thisParamSettings.read_only;
        cellSettings.description="If this Parameter is set to read-only, the default value will always be used.";
        break;
      case ParameterSettingsField.Required:
        cellSettings.isBoolean = true;
        cellSettings.value = thisParamSettings.required;
        cellSettings.description="If this Parameter is required, it will be marked as invalid in the UI if there is no value set.";
        break;
      case ParameterSettingsField.DisplayOrder:
        cellSettings.isReadOnly = true;
        cellSettings.value = thisParam.order;
        cellSettings.description="Display order is shared across all Scan Types, and is not ediable here.";
        break;
    }

    return cellSettings;
  }


  const editableTableFunctions = {
    renderHeaderCell: renderHeaderCell,
    getTableCellSettings: getTableCellSettings,
    isCellValidForEditing: isCellValidForEditing,
    cellValidate: cellValidator,
    cellSave: cellSave,
    onCopy: tableOnCopy,
    onPaste: tableOnPaste,
    rowSelectionChanged: tableRowSelectionChanged
  };


  return (
    <>
      <nav className="level">
        <div className="level-left">
          <div className="level-item">
            <p className="subtitle is-5"><strong>{ sortedParameterSettingsIds.length }</strong> parameters in { scanType ? scanType.name : "scan type" }</p>
          </div>
          <div className="level-item">
            <AddParameterSettings scantypeslug={props.scantypeslug} />
          </div>
        </div>

        <div className="level-right">
          <div className="level-item">
            <div className="dropdown is-right is-hoverable">
              <div className="dropdown-trigger">
                <button className="button is-small is-ghost" aria-haspopup="true" aria-controls="dropdown-menu-columns">
                  <p className="subtitle is-6">Columns</p>
                  <span className="icon is-small">
                    <FontAwesomeIcon icon={faAngleDown} />
                  </span>
                </button>
              </div>
              <div className="dropdown-menu" id="dropdown-menu-columns" role="menu">
                <div className="dropdown-content">
                  { parameterSettingsFieldDisplayOrder.map((_, index) => {
                    return (
                      <a href="#" className="dropdown-item" onClick={ () => { toggleHiddenColumn(index) } }>
                        <FontAwesomeIcon icon={hiddenColumnSet.has(index) ? faSquare : faCheckSquare} />
                        <span style={{ marginLeft: "0.5em" }}>{ columnNames[index] }</span>
                      </a>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
          { selectedParameterSettingsIds.length > 0 && (
            <div className="level-item">
              <div className="dropdown is-right is-hoverable">
                <div className="dropdown-trigger">
                  <button className="button is-small" aria-haspopup="true" aria-controls="dropdown-menu-parameters">
                    <p className="subtitle is-6">{ selectedParameterSettingsIds.length } selected parameter{selectedParameterSettingsIds.length > 1 ? 's' : ''}</p>
                    <span className="icon is-small">
                      <FontAwesomeIcon icon={faAngleDown} />
                    </span>
                  </button>
                </div>
                <div className="dropdown-menu" id="dropdown-menu-parameters" role="menu">
                  <div className="dropdown-content">
                    <RemoveParameterSettings selection={selectedParameterSettingsIds} />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </nav>

      <div className="block" style={{ overflowX: "auto" }}>
        { sortedParameterSettingsIds.length == 0 ? (
          <p>( Use the Add button to add parameters. )</p>
        ) : (
          <>
            <EditableTable
                rowIdentifiers={ sortedParameterSettingsIds as unknown as string[] }
                columnCount={ FIXED_COLUMN_COUNT }
                displayRowSelectionBoxes={ true }
                hiddenColumns={ Array.from(hiddenColumnSet) }
                tableFunctions={ editableTableFunctions }
              />
          </>
        )}
      </div>
    </>
  )
}

export default ParameterTable
